import { useEffect, useState } from 'react';
import axios from 'axios';
import Reveal from '../components/site/Reveal';
import { useT } from '../i18n/LanguageContext';
import { FileText, Download, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseStudies() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/case-studies`).then((r) => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  const openCase = (cs) => {
    if (cs.pdf_url) { window.open(cs.pdf_url, '_blank'); return; }
    if (cs.pdf_base64) {
      const w = window.open();
      if (w) w.document.body.innerHTML = `<iframe src="${cs.pdf_base64}" style="border:0;width:100vw;height:100vh"></iframe>`;
    }
  };

  return (
    <div data-testid="page-casestudies">
      <section className="pt-40 pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <p className="eyebrow mb-6">— Case Studies</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter text-prim max-w-4xl">
              Selected work, in detail.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="py-16 border-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          {loading ? (
            <p className="text-sec font-mono text-sm">{t('common.loading')}</p>
          ) : items.length === 0 ? (
            <p className="text-sec">Case studies will be published here.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((c, i) => (
                <Reveal key={c.id} delay={(i % 3) * 80}>
                  <div data-testid={`case-${c.id}`} className="border border-subtle surface-2 rounded-md card-glow overflow-hidden h-full flex flex-col">
                    {c.cover_base64 ? (
                      <div className="aspect-[3/2] overflow-hidden">
                        <img src={c.cover_base64} alt={c.title} className="photo-fit" />
                      </div>
                    ) : (
                      <div className="aspect-[3/2] flex items-center justify-center bg-gradient-to-br from-brand-maroon/10 to-brand-teal/10">
                        <FileText size={40} className="text-brand-teal-light" />
                      </div>
                    )}
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="font-display text-xl text-prim tracking-tight leading-snug">{c.title}</h3>
                      {c.description && <p className="mt-3 text-sec text-sm leading-relaxed flex-1">{c.description}</p>}
                      <div className="mt-5 flex gap-3">
                        {(c.pdf_url || c.pdf_base64) && (
                          <button onClick={() => openCase(c)} className="btn-secondary text-xs">
                            <Download size={12} /> {t('common.download')}
                          </button>
                        )}
                        {c.pdf_url && (
                          <a href={c.pdf_url} target="_blank" rel="noreferrer" className="btn-secondary text-xs">
                            <ExternalLink size={12} /> Open
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
